/**
 * SemesterCloseOutDialog — the closing checklist for a semester.
 *
 * Closing locks the results (no more mark entry or re-grading) and can carry
 * every unpaid balance forward into the next semester. Re-runnable: balances
 * are never carried twice. A super admin can re-open a closed semester.
 *
 * GET  /tenants/{tid}/terms/{id}/closeout-checklist
 * POST /tenants/{tid}/terms/{id}/close
 * POST /tenants/{tid}/terms/{id}/reopen
 */
import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { CheckCircle2, AlertTriangle, Loader2, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import { schoolApi as api } from '@/components/school/schoolApi';

export const SemesterCloseOutDialog = ({ tenantId, term, terms = [], years = [], onClose }) => {
  const queryClient = useQueryClient();
  const [carry, setCarry] = useState(true);
  const [target, setTarget] = useState('');
  const [notes, setNotes] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['semester-closeout', tenantId, term?.id],
    queryFn: () => api(`/tenants/${tenantId}/terms/${term.id}/closeout-checklist`),
    enabled: !!term?.id,
  });

  useEffect(() => {
    if (data?.next_semester?.id) setTarget(data.next_semester.id);
  }, [data]);

  const invalidate = () => {
    ['school-terms', 'school-academic-structure', 'semester-closeout',
      'fee-structures', 'student-semester-view'].forEach((k) =>
      queryClient.invalidateQueries({ queryKey: [k] }));
  };

  const closeMutation = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/terms/${term.id}/close`, {
      method: 'POST',
      body: JSON.stringify({ carry_forward_balances: carry,
        target_term_id: carry ? (target || null) : null, notes }),
    }),
    onSuccess: (r) => {
      const c = r.carried_forward || {};
      toast.success(
        `${r.semester.name} closed · results locked`
        + (c.count ? ` · ${c.count} balance(s) totalling ${c.total} carried into ${c.target?.name}`
          : ' · no balances to carry'));
      invalidate();
      onClose();
    },
    onError: (e) => toast.error(e?.message || 'Could not close the semester'),
  });

  const reopenMutation = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/terms/${term.id}/reopen`, { method: 'POST' }),
    onSuccess: (r) => {
      toast.success(`${r.semester.name} re-opened · results unlocked`
        + (r.carried_charges_removed ? ` · ${r.carried_charges_removed} carried charge(s) removed` : ''));
      invalidate();
      onClose();
    },
    onError: (e) => toast.error(e?.message || 'Could not re-open the semester'),
  });

  const closed = data?.semester?.status === 'closed' || data?.semester?.results_locked;
  const others = terms.filter((t) => t.id !== term?.id);

  return (
    <Dialog open={!!term} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="closeout-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {closed ? <Lock className="h-4 w-4 text-slate-500" />
              : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
            Close out {term?.name}
          </DialogTitle>
          <DialogDescription>
            Work through the checklist, then lock the results and carry unpaid balances forward.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? <Loader2 className="h-6 w-6 animate-spin mx-auto my-8" /> : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            {closed && (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                   data-testid="closeout-closed-banner">
                This semester is <strong>closed</strong> and its results are locked
                {data?.semester?.closed_at ? ` since ${String(data.semester.closed_at).slice(0, 10)}` : ''}.
                Mark entry and re-grading are refused until it is re-opened.
              </div>
            )}

            <div className="space-y-2" data-testid="closeout-checklist">
              {(data?.items || []).map((item) => (
                <div key={item.key}
                     className="flex items-start gap-2 rounded-md border px-3 py-2"
                     data-testid={`closeout-item-${item.key}`}>
                  {item.ok
                    ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                    : <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800">{item.label}</p>
                    <p className="text-xs text-slate-500">{item.detail}</p>
                    {(item.offenders || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {item.offenders.slice(0, 6).map((o) => (
                          <Badge key={o.id} variant="outline" className="text-[10px]">{o.name}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {!closed && (
              <div className="rounded-md border p-3 space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={carry} data-testid="closeout-carry-toggle"
                         onChange={(e) => setCarry(e.target.checked)} />
                  Carry unpaid balances forward
                </label>
                <p className="text-xs text-slate-500">
                  {data?.balances?.length
                    ? `${data.balances.length} student(s) owe ${Number(data.outstanding_total).toLocaleString()} in ${term?.name}.`
                    : 'Nothing outstanding — nothing will be carried.'}
                  {data?.already_carried
                    ? ` ${data.already_carried} balance(s) were already carried before.`
                    : ''}
                </p>
                {carry && (
                  <div className="space-y-1.5">
                    <Label>Carry into</Label>
                    <select
                      value={target} onChange={(e) => setTarget(e.target.value)}
                      data-testid="closeout-target-select"
                      className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                    >
                      <option value="">— next semester —</option>
                      {others.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                          {(() => {
                            const y = years.find((x) => x.id === t.academic_year_id);
                            return y && y.id !== term?.academic_year_id ? ` · ${y.name}` : '';
                          })()}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {(data?.balances || []).length > 0 && (
                  <div className="max-h-40 overflow-y-auto rounded border"
                       data-testid="closeout-balances">
                    {data.balances.slice(0, 50).map((b) => (
                      <div key={b.student_id}
                           className="flex items-center justify-between px-3 py-1.5 text-xs border-b last:border-0">
                        <span className="text-slate-700">{b.student_name}</span>
                        <span className="font-medium text-rose-600">
                          {Number(b.balance).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="closeout-notes">Notes (optional)</Label>
                  <Input id="closeout-notes" value={notes} data-testid="closeout-notes"
                         onChange={(e) => setNotes(e.target.value)}
                         placeholder="Anything the next bursar should know" />
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {closed ? (
            <Button
              variant="outline" data-testid="closeout-reopen"
              disabled={reopenMutation.isPending}
              onClick={() => {
                if (window.confirm(`Re-open ${term?.name}? Results become editable again and any unpaid carried-forward charge is removed.`)) {
                  reopenMutation.mutate();
                }
              }}
            >
              <Unlock className="h-4 w-4 mr-1" />
              {reopenMutation.isPending ? 'Re-opening…' : 'Re-open semester'}
            </Button>
          ) : (
            <Button
              data-testid="closeout-confirm" disabled={closeMutation.isPending}
              onClick={() => closeMutation.mutate()}
            >
              <Lock className="h-4 w-4 mr-1" />
              {closeMutation.isPending ? 'Closing…' : 'Close semester'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SemesterCloseOutDialog;
